import React, { useState, useEffect, useCallback, useRef } from 'react'
import { fetchCallbacks, patchCallback, createCallback } from '../../services/telephonyApi'

/**
 * CallbacksCard — My Callbacks widget
 *
 * Card design (per reference screenshot):
 *  - Due / Today cards: dark navy left-border accent, "Mark Complete" navy pill + calendar icon
 *  - Upcoming cards: clean card, soft blue "Reschedule" pill + delete icon
 *  - Inline Reschedule editor: opens on calendar icon click
 *  - Add form: triggered by + header button
 *
 * All buttons fixed: proper type="button", state management, error feedback, and
 * datetime-local → Python datetime conversion handled by backend (_parse_scheduled_at).
 */
export default function CallbacksCard() {
  const [callbacks, setCallbacks] = useState([])
  const [loading, setLoading] = useState(true)
  const [listError, setListError] = useState(null)

  // ── Add Callback Form ──────────────────────────────────────────────────────
  const [showAddForm, setShowAddForm] = useState(false)
  const [addForm, setAddForm] = useState({
    contact_name: '', contact_number: '', scheduled_at: '', notes: '',
  })
  const [addSubmitting, setAddSubmitting] = useState(false)
  const [addError, setAddError] = useState(null)

  // ── Reschedule state (keyed by callback id) ────────────────────────────────
  const [reschedulingId, setReschedulingId] = useState(null)
  const [rescheduleForm, setRescheduleForm] = useState({ scheduled_at: '', notes: '' })
  const [rescheduleSubmitting, setRescheduleSubmitting] = useState(false)
  const [rescheduleError, setRescheduleError] = useState(null)

  // ── Per-card action in-flight (completing | deleting) ─────────────────────
  const [actioning, setActioning] = useState({})

  const isMounted = useRef(true)
  useEffect(() => {
    isMounted.current = true
    return () => { isMounted.current = false }
  }, [])

  // ── Data loading ───────────────────────────────────────────────────────────
  const loadCallbacks = useCallback(async () => {
    try {
      setLoading(true)
      setListError(null)
      const res = await fetchCallbacks('PENDING')
      if (isMounted.current) setCallbacks(res?.data || [])
    } catch (err) {
      console.error('Failed to load callbacks:', err)
      if (isMounted.current) setListError('Could not load callbacks.')
    } finally {
      if (isMounted.current) setLoading(false)
    }
  }, [])

  useEffect(() => { loadCallbacks() }, [loadCallbacks])

  // ── Actions ────────────────────────────────────────────────────────────────

  const setActionInFlight = (id, state) =>
    setActioning((prev) => ({ ...prev, [id]: state }))

  const handleMarkComplete = async (cb) => {
    setActionInFlight(cb.id, 'completing')
    try {
      await patchCallback(cb.id, 'complete')
      if (isMounted.current)
        setCallbacks((prev) => prev.filter((c) => c.id !== cb.id))
    } catch (err) {
      console.error('Mark complete failed:', err)
    } finally {
      if (isMounted.current) setActionInFlight(cb.id, false)
    }
  }

  const handleDeleteCallback = async (cb) => {
    setActionInFlight(cb.id, 'deleting')
    try {
      await patchCallback(cb.id, 'cancel')
      if (isMounted.current)
        setCallbacks((prev) => prev.filter((c) => c.id !== cb.id))
    } catch (err) {
      console.error('Delete callback failed:', err)
    } finally {
      if (isMounted.current) setActionInFlight(cb.id, false)
    }
  }

  // ── Reschedule inline editor ───────────────────────────────────────────────

  const openReschedule = (cb) => {
    setReschedulingId(cb.id)
    setRescheduleError(null)
    let defaultTime = ''
    try {
      let d = cb.scheduled_at ? new Date(cb.scheduled_at) : new Date(Date.now() + 60000)
      if (Number.isNaN(d.getTime()) || d.getTime() <= Date.now()) {
        d = new Date(Date.now() + 60000)
      }
      const pad = (n) => String(n).padStart(2, '0')
      defaultTime = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    } catch {
      const d = new Date(Date.now() + 60000)
      const pad = (n) => String(n).padStart(2, '0')
      defaultTime = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    }
    setRescheduleForm({ scheduled_at: defaultTime, notes: cb.notes || '' })
  }

  const closeReschedule = () => {
    setReschedulingId(null)
    setRescheduleError(null)
    setRescheduleSubmitting(false)
  }

  const handleRescheduleSubmit = async (cb) => {
    if (!rescheduleForm.scheduled_at) return
    setRescheduleSubmitting(true)
    setRescheduleError(null)
    try {
      await patchCallback(cb.id, 'reschedule', {
        scheduled_at: rescheduleForm.scheduled_at,  // backend _parse_scheduled_at handles string
        notes: rescheduleForm.notes,
      })
      if (isMounted.current) {
        closeReschedule()
        loadCallbacks()
      }
    } catch (err) {
      console.error('Reschedule failed:', err)
      if (isMounted.current)
        setRescheduleError(err?.response?.data?.detail || 'Failed to reschedule. Please try again.')
    } finally {
      if (isMounted.current) setRescheduleSubmitting(false)
    }
  }

  // ── Add callback form ──────────────────────────────────────────────────────

  const openAddForm = () => {
    setShowAddForm(true)
    setAddError(null)
    const d = new Date(Date.now() + 60000)
    const pad = (n) => String(n).padStart(2, '0')
    const defaultTime = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    setAddForm({ contact_name: '', contact_number: '', scheduled_at: defaultTime, notes: '' })
    setReschedulingId(null)
  }

  const closeAddForm = () => {
    setShowAddForm(false)
    setAddError(null)
    setAddSubmitting(false)
  }

  const handleAddSubmit = async (e) => {
    e.preventDefault()
    if (!addForm.contact_number.trim() || !addForm.scheduled_at) return
    setAddSubmitting(true)
    setAddError(null)
    try {
      await createCallback({
        contact_name: addForm.contact_name.trim(),
        contact_number: addForm.contact_number.trim(),
        scheduled_at: addForm.scheduled_at,  // backend parses datetime-local string safely
        notes: addForm.notes.trim(),
      })
      if (isMounted.current) {
        closeAddForm()
        loadCallbacks()
      }
    } catch (err) {
      console.error('Create callback failed:', err)
      if (isMounted.current)
        setAddError(err?.response?.data?.detail || 'Failed to schedule callback. Please try again.')
    } finally {
      if (isMounted.current) setAddSubmitting(false)
    }
  }

  // ── Formatting helpers ─────────────────────────────────────────────────────

  /**
   * Classify whether a callback is "due" (overdue or due today / within a few hours)
   * vs "upcoming" (tomorrow or later). This drives card styling and action button choice.
   */
  const getCountdownInfo = (scheduledAt) => {
    try {
      const diff = new Date(scheduledAt).getTime() - Date.now()
      if (diff < 0) return { label: 'Overdue', isDue: true, isOverdue: true }
      const mins = Math.floor(diff / 60000)
      const hours = Math.floor(mins / 60)
      const days = Math.floor(hours / 24)
      if (mins < 60) return { label: `In ${mins}m`, isDue: true, isOverdue: false }
      if (hours < 24) return { label: `In ${hours}h`, isDue: true, isOverdue: false }
      if (days === 1) return { label: 'Tomorrow', isDue: false, isOverdue: false }
      return { label: `In ${days}d`, isDue: false, isOverdue: false }
    } catch {
      return { label: '', isDue: false, isOverdue: false }
    }
  }

  const formatTime = (scheduledAt) => {
    try {
      const d = new Date(scheduledAt)
      const today = new Date()
      const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1)
      const isToday = d.toDateString() === today.toDateString()
      const isTomorrow = d.toDateString() === tomorrow.toDateString()
      const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      if (isToday) return `Today, ${time}`
      if (isTomorrow) return time
      return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${time}`
    } catch { return scheduledAt }
  }

  const pendingCount = callbacks.length

  return (
    <div className="xl:col-span-5 bg-surface-container-lowest rounded-2xl border border-outline-variant/25 shadow-xs flex flex-col">

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-outline-variant/15">
        <div>
          <h2 className="text-sm font-extrabold uppercase tracking-wider font-display text-on-surface">
            My Callbacks
          </h2>
          <p className="text-[11px] text-on-surface-variant font-medium mt-0.5">
            Scheduled agent follow-ups.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {pendingCount > 0 && (
            <span className="px-3 py-1 rounded-full bg-[#002b66] text-white text-[10px] font-black uppercase tracking-wider">
              {pendingCount} DUE
            </span>
          )}
          {/* Add callback */}
          <button
            type="button"
            onClick={openAddForm}
            title="Schedule new callback"
            className="w-7 h-7 rounded-xl bg-surface-container-low border border-outline-variant/30 text-on-surface-variant hover:bg-[#003B96]/10 hover:text-[#003B96] flex items-center justify-center transition-colors cursor-pointer"
          >
            <span className="material-symbols-outlined text-sm">add</span>
          </button>
          {/* Refresh */}
          {/* <button
            type="button"
            onClick={loadCallbacks}
            disabled={loading}
            title="Refresh"
            className="w-7 h-7 rounded-xl bg-surface-container-low border border-outline-variant/30 text-on-surface-variant hover:bg-surface-container-highest hover:text-primary flex items-center justify-center transition-colors cursor-pointer disabled:opacity-50"
          >
            <span className={`material-symbols-outlined text-sm ${loading ? 'animate-spin' : ''}`}>refresh</span>
          </button> */}
        </div>
      </div>

      {/* ── Add Callback Form ──────────────────────────────────────────────── */}
      {showAddForm && (
        <form
          onSubmit={handleAddSubmit}
          className="mx-5 mt-4 p-4 rounded-2xl border border-[#003B96]/20 bg-[#003B96]/5 space-y-2.5"
        >
          <p className="text-xs font-extrabold text-[#003B96] uppercase tracking-wider mb-1">
            Schedule Callback
          </p>

          {addError && (
            <div className="flex items-start gap-1.5 p-2.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs">
              <span className="material-symbols-outlined text-xs shrink-0 mt-0.5">error</span>
              <span>{addError}</span>
            </div>
          )}

          <input
            type="text"
            placeholder="Contact name (optional)"
            value={addForm.contact_name}
            onChange={(e) => setAddForm((f) => ({ ...f, contact_name: e.target.value }))}
            className="w-full px-3 py-2 rounded-xl border border-outline-variant/40 text-xs bg-surface-container-lowest text-on-surface placeholder:text-on-surface-variant/60 focus:outline-none focus:ring-2 focus:ring-[#003B96]/30"
          />
          <input
            type="tel"
            placeholder="Phone number *"
            required
            value={addForm.contact_number}
            onChange={(e) => setAddForm((f) => ({ ...f, contact_number: e.target.value }))}
            className="w-full px-3 py-2 rounded-xl border border-outline-variant/40 text-xs bg-surface-container-lowest text-on-surface placeholder:text-on-surface-variant/60 focus:outline-none focus:ring-2 focus:ring-[#003B96]/30"
          />
          <input
            type="datetime-local"
            required
            value={addForm.scheduled_at}
            onChange={(e) => setAddForm((f) => ({ ...f, scheduled_at: e.target.value }))}
            className="w-full px-3 py-2 rounded-xl border border-outline-variant/40 text-xs bg-surface-container-lowest text-on-surface focus:outline-none focus:ring-2 focus:ring-[#003B96]/30"
          />
          <div className="flex gap-1.5 flex-wrap pt-0.5">
            <button
              type="button"
              className="text-[10px] px-2 py-0.5 rounded-md bg-[#003B96]/10 text-[#003B96] font-semibold hover:bg-[#003B96]/20 transition-colors cursor-pointer"
              onClick={() => {
                const d = new Date()
                const pad = (n) => String(n).padStart(2, '0')
                setAddForm((f) => ({ ...f, scheduled_at: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}` }))
              }}
            >
              Due Now
            </button>
            <button
              type="button"
              className="text-[10px] px-2 py-0.5 rounded-md bg-[#003B96]/10 text-[#003B96] font-semibold hover:bg-[#003B96]/20 transition-colors cursor-pointer"
              onClick={() => {
                const d = new Date(Date.now() + 60000)
                const pad = (n) => String(n).padStart(2, '0')
                setAddForm((f) => ({ ...f, scheduled_at: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}` }))
              }}
            >
              +1 Min
            </button>
            <button
              type="button"
              className="text-[10px] px-2 py-0.5 rounded-md bg-[#003B96]/10 text-[#003B96] font-semibold hover:bg-[#003B96]/20 transition-colors cursor-pointer"
              onClick={() => {
                const d = new Date(Date.now() + 300000)
                const pad = (n) => String(n).padStart(2, '0')
                setAddForm((f) => ({ ...f, scheduled_at: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}` }))
              }}
            >
              +5 Min
            </button>
          </div>
          <textarea
            placeholder="Notes (optional)"
            rows={2}
            value={addForm.notes}
            onChange={(e) => setAddForm((f) => ({ ...f, notes: e.target.value }))}
            className="w-full px-3 py-2 rounded-xl border border-outline-variant/40 text-xs bg-surface-container-lowest text-on-surface placeholder:text-on-surface-variant/60 focus:outline-none focus:ring-2 focus:ring-[#003B96]/30 resize-none"
          />
          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={addSubmitting}
              className="flex-1 py-2 rounded-full bg-[#003B96] hover:bg-[#002F78] text-white text-xs font-bold transition-colors disabled:opacity-60 cursor-pointer"
            >
              {addSubmitting ? 'Scheduling…' : 'Schedule'}
            </button>
            <button
              type="button"
              onClick={closeAddForm}
              disabled={addSubmitting}
              className="flex-1 py-2 rounded-full border border-outline-variant/30 text-xs font-semibold text-on-surface-variant hover:bg-surface-container-low transition-colors cursor-pointer disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* ── Callback List ──────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-5 pb-5 space-y-3 pt-4 max-h-[540px]">

        {listError && (
          <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs">
            <span className="material-symbols-outlined text-xs">error</span>
            {listError}
            <button type="button" onClick={loadCallbacks} className="ml-auto font-bold underline cursor-pointer">
              Retry
            </button>
          </div>
        )}

        {loading && !listError ? (
          [1, 2].map((i) => (
            <div key={i} className="h-24 rounded-2xl bg-surface-container-low animate-pulse" />
          ))
        ) : callbacks.length === 0 && !listError ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <span className="material-symbols-outlined text-4xl text-outline-variant mb-2">phone_callback</span>
            <p className="text-xs font-bold text-on-surface">No Pending Callbacks</p>
            <p className="text-[11px] text-on-surface-variant mt-0.5">
              Scheduled customer callbacks will appear here.
            </p>
          </div>
        ) : (
          callbacks.map((cb) => {
            const { label: countdown, isDue, isOverdue } = getCountdownInfo(cb.scheduled_at)
            const isActioning = actioning[cb.id]
            const isRescheduling = reschedulingId === cb.id

            return (
              <div key={cb.id} className="relative">

                {/* ── Card ────────────────────────────────────────────── */}
                <div
                  className={`relative rounded-2xl bg-white border border-outline-variant/20 shadow-2xs overflow-hidden ${isDue ? 'border-l-4 border-l-[#003882]' : ''
                    }`}
                >
                  <div className="px-4 py-3.5 space-y-2">

                    {/* Row 1: Name + Countdown */}
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="text-sm font-bold text-on-surface font-display leading-tight">
                          {cb.contact_name || cb.contact_number}
                        </h3>
                        {cb.contact_name && (
                          <span className="text-[11px] text-on-surface-variant">{cb.contact_number}</span>
                        )}
                      </div>
                      <span className={`text-[11px] font-extrabold whitespace-nowrap pt-0.5 ${isOverdue ? 'text-red-600' : isDue ? 'text-[#1a56db]' : 'text-on-surface-variant'
                        }`}>
                        {countdown}
                      </span>
                    </div>

                    {/* Row 2: Time with clock icon */}
                    <div className="flex items-center gap-1.5 text-[11px] text-on-surface-variant">
                      <span className="material-symbols-outlined text-xs">schedule</span>
                      <span>{formatTime(cb.scheduled_at)}</span>
                      {cb.auto_created && (
                        <span className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[9px] font-bold border border-amber-200">
                          <span className="material-symbols-outlined text-[9px]">phone_missed</span>
                          Auto
                        </span>
                      )}
                    </div>

                    {/* Row 3: Notes */}
                    {cb.notes && (
                      <p className="text-[11px] italic text-on-surface-variant/80 leading-relaxed line-clamp-2">
                        &ldquo;{cb.notes}&rdquo;
                      </p>
                    )}

                    {/* Row 4: Action Buttons (hidden when reschedule editor is open) */}
                    {!isRescheduling && (
                      <div className="flex items-center gap-2 pt-0.5">
                        {isDue ? (
                          <>
                            {/* Mark Complete — primary action for due/overdue cards */}
                            <button
                              type="button"
                              disabled={!!isActioning}
                              onClick={() => handleMarkComplete(cb)}
                              className="flex-1 py-2 rounded-full bg-[#003882] hover:bg-[#002b66] text-white font-bold text-xs shadow-2xs transition-colors disabled:opacity-60 cursor-pointer"
                            >
                              {isActioning === 'completing' ? 'Completing…' : 'Mark Complete'}
                            </button>
                            {/* Calendar reschedule icon button */}
                            <button
                              type="button"
                              disabled={!!isActioning}
                              onClick={() => openReschedule(cb)}
                              title="Reschedule"
                              className="w-8 h-8 rounded-lg border border-outline-variant/40 bg-surface-container-lowest hover:bg-[#e3edfc] hover:border-[#1a56db]/30 hover:text-[#1a56db] text-on-surface-variant flex items-center justify-center transition-colors shrink-0 disabled:opacity-60 cursor-pointer"
                            >
                              <span className="material-symbols-outlined text-sm">edit_calendar</span>
                            </button>
                          </>
                        ) : (
                          <>
                            {/* Reschedule — primary action for upcoming callbacks */}
                            <button
                              type="button"
                              disabled={!!isActioning}
                              onClick={() => openReschedule(cb)}
                              className="flex-1 py-2 rounded-full bg-[#e3edfc] hover:bg-[#cfe0fa] text-[#1a56db] font-bold text-xs transition-colors disabled:opacity-60 cursor-pointer"
                            >
                              Reschedule
                            </button>
                            {/* Delete / Cancel callback icon button */}
                            <button
                              type="button"
                              disabled={!!isActioning}
                              onClick={() => handleDeleteCallback(cb)}
                              title="Cancel Callback"
                              className="w-8 h-8 rounded-lg border border-outline-variant/40 bg-surface-container-lowest hover:bg-red-50 hover:border-red-200 hover:text-red-600 text-on-surface-variant flex items-center justify-center transition-colors shrink-0 disabled:opacity-60 cursor-pointer"
                            >
                              {isActioning === 'deleting'
                                ? <span className="material-symbols-outlined text-sm animate-spin">refresh</span>
                                : <span className="material-symbols-outlined text-sm">delete</span>
                              }
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Inline Reschedule Editor (below card, slides in) ─── */}
                {isRescheduling && (
                  <div className="mt-2 p-3.5 rounded-2xl border border-[#1a56db]/20 bg-[#e3edfc]/40 space-y-2.5">
                    <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#1a56db]">
                      Reschedule Callback
                    </p>

                    {rescheduleError && (
                      <div className="flex items-start gap-1.5 p-2 rounded-xl bg-red-50 border border-red-200 text-red-700 text-[11px]">
                        <span className="material-symbols-outlined text-xs shrink-0 mt-0.5">error</span>
                        <span>{rescheduleError}</span>
                      </div>
                    )}

                    <input
                      type="datetime-local"
                      value={rescheduleForm.scheduled_at}
                      onChange={(e) => setRescheduleForm((f) => ({ ...f, scheduled_at: e.target.value }))}
                      className="w-full px-2.5 py-1.5 rounded-xl border border-outline-variant/40 text-xs bg-surface-container-lowest focus:outline-none focus:ring-2 focus:ring-[#1a56db]/30"
                    />
                    <div className="flex gap-1.5 flex-wrap pt-0.5">
                      <button
                        type="button"
                        className="text-[10px] px-2 py-0.5 rounded-md bg-[#1a56db]/10 text-[#1a56db] font-semibold hover:bg-[#1a56db]/20 transition-colors cursor-pointer"
                        onClick={() => {
                          const d = new Date()
                          const pad = (n) => String(n).padStart(2, '0')
                          setRescheduleForm((f) => ({ ...f, scheduled_at: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}` }))
                        }}
                      >
                        Due Now
                      </button>
                      <button
                        type="button"
                        className="text-[10px] px-2 py-0.5 rounded-md bg-[#1a56db]/10 text-[#1a56db] font-semibold hover:bg-[#1a56db]/20 transition-colors cursor-pointer"
                        onClick={() => {
                          const d = new Date(Date.now() + 60000)
                          const pad = (n) => String(n).padStart(2, '0')
                          setRescheduleForm((f) => ({ ...f, scheduled_at: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}` }))
                        }}
                      >
                        +1 Min
                      </button>
                      <button
                        type="button"
                        className="text-[10px] px-2 py-0.5 rounded-md bg-[#1a56db]/10 text-[#1a56db] font-semibold hover:bg-[#1a56db]/20 transition-colors cursor-pointer"
                        onClick={() => {
                          const d = new Date(Date.now() + 300000)
                          const pad = (n) => String(n).padStart(2, '0')
                          setRescheduleForm((f) => ({ ...f, scheduled_at: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}` }))
                        }}
                      >
                        +5 Min
                      </button>
                    </div>
                    <textarea
                      placeholder="Updated notes (optional)"
                      rows={2}
                      value={rescheduleForm.notes}
                      onChange={(e) => setRescheduleForm((f) => ({ ...f, notes: e.target.value }))}
                      className="w-full px-2.5 py-1.5 rounded-xl border border-outline-variant/40 text-xs bg-surface-container-lowest focus:outline-none focus:ring-1 focus:ring-[#1a56db]/30 resize-none"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={rescheduleSubmitting || !rescheduleForm.scheduled_at}
                        onClick={() => handleRescheduleSubmit(cb)}
                        className="flex-1 py-1.5 rounded-full bg-[#003882] hover:bg-[#002b66] text-white text-xs font-bold transition-colors disabled:opacity-60 cursor-pointer"
                      >
                        {rescheduleSubmitting ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        type="button"
                        disabled={rescheduleSubmitting}
                        onClick={closeReschedule}
                        className="flex-1 py-1.5 rounded-full border border-outline-variant/30 text-xs font-semibold text-on-surface-variant hover:bg-surface-container-low transition-colors cursor-pointer disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
